import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  useTheme,
  alpha,
  Stack,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Check,
  Close,
  Timer,
  Security,
  Star,
  Inventory2,
  VolumeUp,
  TouchApp,
  BatteryFull,
  WaterDrop
} from '@mui/icons-material';
import { useTranslate } from '@refinedev/core';
import CheckoutButton from '../../pricing/CheckoutButton';

interface PaywallWalaxyProps {
  onComplete: () => void;
  companyData: any;
  invitedUsers: any[];
}

interface PricingPlan {
  id: string;
  name: string;
  stripeKey: 'pro' | 'pro_case' | 'event';
  popular?: boolean;
  price?: string;
  priceLabel?: string;
  totalPrice?: string;
  features: Array<{ text: string; available: boolean }>;
  hasDevice?: boolean;
  deviceFeatures?: Array<{ icon: any; text: string }>;
  buttonText: string;
  color: string;
}

export const PaywallWalaxy: React.FC<PaywallWalaxyProps> = ({ onComplete, companyData, invitedUsers }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [isAnnual, setIsAnnual] = useState(false);
  const [numberOfUsers, setNumberOfUsers] = useState(Math.max(invitedUsers.length + 1, 10));

  // Memoized plans
  const plans = useMemo(() => {
    const basePrice = isAnnual ? 5.37 : 10.75;
    const casePrice = isAnnual ? 6 : 9;

    const proPricePerUser = basePrice;
    const proCasePricePerUser = basePrice + casePrice;

    const proMonthlyTotal = proPricePerUser * numberOfUsers;
    const proAnnualTotal = proPricePerUser * numberOfUsers * 12;

    const proCaseMonthlyTotal = proCasePricePerUser * numberOfUsers;
    const proCaseAnnualTotal = proCasePricePerUser * numberOfUsers * 12;

    // Core Safee B2B features for all Pro plans
    const commonFeatures = [
      { text: 'Alertes SOS en temps réel', available: true },
      { text: 'Géolocalisation en direct', available: true },
      { text: 'Diffusion vidéo d\'urgence', available: true },
      { text: 'Dashboard administrateur', available: true },
      { text: 'Historique des incidents', available: true },
      { text: 'Notifications immédiates', available: true }
    ];

    const plansList: PricingPlan[] = [
      {
        id: 'pro',
        name: 'Pro',
        stripeKey: 'pro',
        price: `${proPricePerUser.toFixed(2)}€`,
        priceLabel: `/utilisateur${isAnnual ? '/mois' : '/mois'}`,
        totalPrice: isAnnual
          ? `${proAnnualTotal.toFixed(2)}€/an`
          : `${proMonthlyTotal.toFixed(2)}€/mois`,
        features: [
          ...commonFeatures
        ],
        hasDevice: false,
        buttonText: 'Démarrer l\'essai gratuit',
        color: theme.palette.primary.main
      },
      {
        id: 'pro_case',
        name: 'Pro+',
        stripeKey: 'pro_case',
        popular: true,
        price: `${proCasePricePerUser.toFixed(2)}€`,
        priceLabel: `/utilisateur${isAnnual ? '/mois' : '/mois'}`,
        totalPrice: isAnnual
          ? `${proCaseAnnualTotal.toFixed(2)}€/an`
          : `${proCaseMonthlyTotal.toFixed(2)}€/mois`,
        features: [
          ...commonFeatures
        ],
        hasDevice: true,
        deviceFeatures: [
          { icon: VolumeUp, text: 'Sirène 105 dB' },
          { icon: TouchApp, text: 'Double-clic d\'urgence' },
          { icon: BatteryFull, text: '30 jours d\'autonomie' },
          { icon: WaterDrop, text: 'Étanche IP67' }
        ],
        buttonText: 'Commander avec le boîtier',
        color: theme.palette.secondary.main
      },
      {
        id: 'event',
        name: 'Entreprise',
        stripeKey: 'event',
        price: 'Sur devis',
        priceLabel: '/utilisateur/mois',
        totalPrice: 'Contactez notre équipe commerciale',
        features: [
          { text: 'Utilisateurs illimités', available: true },
          { text: 'Alertes SOS en temps réel', available: true },
          { text: 'Diffusion vidéo d\'urgence', available: true },
          { text: 'Connection avec la sécurité', available: true },
          { text: 'Dashboard administrateur', available: true },
          { text: 'Historique des incidents', available: true },
          { text: 'Notifications immédiates', available: true }
        ],
        hasDevice: true,
        buttonText: 'Demander un devis',
        color: theme.palette.text.secondary
      }
    ];

    return plansList;
  }, [isAnnual, numberOfUsers, theme]);

  return (
    <Box sx={{
      maxWidth: 1200,
      mx: 'auto',
      p: { xs: 2, md: 3 },
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          fontWeight={600}
          sx={{
            mb: 0.5,
            fontSize: { xs: '1.75rem', md: '2rem' },
            letterSpacing: '-0.02em'
          }}
        >
          Choisissez votre protection
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontWeight: 400,
            fontSize: '0.95rem'
          }}
        >
          Solution de sécurité complète pour protéger vos équipes
        </Typography>
      </Box>

      {/* Billing Period Selector */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={isAnnual ? 'annual' : 'monthly'}
          exclusive
          onChange={(e, value) => value && setIsAnnual(value === 'annual')}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: 2.5,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                }
              }
            }
          }}
        >
          <ToggleButton value="monthly">
            Mensuel
          </ToggleButton>
          <ToggleButton value="annual">
            <Stack direction="row" spacing={1} alignItems="center">
              <span>Annuel</span>
              <Chip
                label="-50%"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.7rem',
                  bgcolor: '#22c55e',
                  color: 'white'
                }}
              />
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* User count slider */}
      <Box sx={{
        maxWidth: 500,
        mx: 'auto',
        mb: 3,
        px: 2
      }}>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            textAlign: 'center',
            color: 'text.secondary'
          }}
        >
          Nombre d'utilisateurs: <strong>{numberOfUsers}</strong>
        </Typography>
        <Slider
          value={numberOfUsers}
          onChange={(e, value) => setNumberOfUsers(value as number)}
          min={Math.max(invitedUsers.length + 1, 1)}
          max={200}
          sx={{
            '& .MuiSlider-thumb': {
              bgcolor: theme.palette.primary.main,
              width: 20,
              height: 20,
              '&:hover': {
                boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`,
              }
            },
            '& .MuiSlider-track': {
              bgcolor: theme.palette.primary.main,
              height: 6,
            },
            '& .MuiSlider-rail': {
              bgcolor: alpha(theme.palette.divider, 0.2),
              height: 6,
            }
          }}
        />
      </Box>

      {/* Pricing Cards */}
      <Grid container spacing={3} sx={{ mb: 4, mt: 4, alignItems: 'stretch' }}>
        {plans.map((plan, index) => (
          <Grid item xs={12} md={4} key={plan.id} sx={{ display: 'flex', pt: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{ width: '100%', display: 'flex', position: 'relative' }}
            >
              {/* Popular badge - Outside the card */}
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontWeight: 800,
                    px: 3,
                    py: 1,
                    fontSize: '0.9rem',
                    letterSpacing: '1px',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                    borderRadius: 2,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      },
                      '50%': {
                        boxShadow: `0 6px 28px ${alpha(theme.palette.primary.main, 0.7)}`,
                      },
                      '100%': {
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      },
                    }
                  }}
                >
                  <span>🔥</span>
                  <span>POPULAIRE</span>
                </Box>
              )}
              <Card
                onClick={() => setSelectedPlan(plan.id)}
                sx={{
                  p: 3,
                  width: '100%',
                  minHeight: 650,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  cursor: 'pointer',
                  border: `2px solid ${
                    selectedPlan === plan.id
                      ? plan.color
                      : alpha(theme.palette.divider, 0.2)
                  }`,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  bgcolor: selectedPlan === plan.id
                    ? alpha(plan.color, 0.02)
                    : 'background.paper',
                  '&:hover': {
                    borderColor: plan.color,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(plan.color, 0.1)}`
                  }
                }}
              >

                {/* Plan header */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Price */}
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={700}
                      sx={{
                        fontSize: '2rem',
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {plan.price}
                    </Typography>
                    {plan.priceLabel && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ 
                          mt: 0.5,
                          opacity: plan.id === 'event' ? 0 : 1
                        }}
                      >
                        {plan.priceLabel}
                      </Typography>
                    )}
                  </Box>

                  {/* Total price */}
                  {plan.totalPrice && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        display: 'block'
                      }}
                    >
                      Total: {plan.totalPrice}
                    </Typography>
                  )}
                </Box>

                {/* Hardware Device Section - Show for all plans with same height */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    height: 160,
                    display: 'flex',
                    flexDirection: 'column',
                    background: plan.id === 'pro_case' 
                      ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`
                      : plan.id === 'event'
                      ? `linear-gradient(135deg, ${alpha('#FFD700', 0.05)} 0%, ${alpha('#FFA500', 0.05)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)} 0%, ${alpha(theme.palette.error.main, 0.08)} 100%)`,
                    border: `1px solid ${
                      plan.id === 'pro_case' 
                        ? alpha(theme.palette.success.main, 0.2) 
                        : plan.id === 'event'
                        ? alpha(theme.palette.warning.main, 0.2)
                        : alpha(theme.palette.error.main, 0.2)
                    }`
                  }}
                >
                  {plan.id === 'pro' && (
                    <>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight={700} 
                        color={theme.palette.error.main}
                        sx={{ mb: 1.5 }}
                      >
                        BOÎTIER DE SÉCURITÉ NON INCLUS
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          fontSize: '0.85rem'
                        }}
                      >
                        Passez à Pro+ pour renforcer la sécurité avec le boîtier physique
                      </Typography>
                    </>
                  )}
                  {plan.id === 'pro_case' && (
                    <>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight={700} 
                        color={theme.palette.success.main}
                        sx={{ mb: 1.5 }}
                      >
                        BOÎTIER DE SÉCURITÉ INCLUS
                      </Typography>
                      <Grid container spacing={1.5}>
                        {plan.deviceFeatures?.map((feature, idx) => {
                          const Icon = feature.icon;
                          return (
                            <Grid item xs={6} key={idx}>
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    flexShrink: 0
                                  }}
                                >
                                  <Icon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                                </Box>
                                <Typography sx={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: 500,
                                  lineHeight: 1.3,
                                  pt: 0.3
                                }}>
                                  {feature.text}
                                </Typography>
                              </Stack>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  )}
                  {plan.id === 'event' && (
                    <>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight={700} 
                        color={theme.palette.warning.dark}
                        sx={{ mb: 1.5 }}
                      >
                        BOÎTIERS DISPONIBLES SUR DEMANDE
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.85rem'
                        }}
                      >
                        Quantités personnalisées et configuration sur-mesure pour votre organisation
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Software Features Section */}
                <Box sx={{ flex: 1, mb: 3 }}>
                  {(plan.id === 'pro_case' || plan.id === 'pro') && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        mb: 1.5,
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Fonctionnalités de l'application
                    </Typography>
                  )}
                  <List dense sx={{ py: 0 }}>
                    {plan.features.slice(0, plan.id === 'event' ? 11 : 11).map((feature, idx) => (
                      <ListItem 
                        key={idx} 
                        sx={{ 
                          px: 0, 
                          py: 0.4,
                          opacity: feature.available ? 1 : 0.7
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {feature.available ? (
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Check 
                                sx={{ 
                                  fontSize: 12,
                                  color: theme.palette.success.main
                                }} 
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Close 
                                sx={{ 
                                  fontSize: 12,
                                  color: theme.palette.error.main
                                }} 
                              />
                            </Box>
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: feature.available ? 500 : 400,
                            color: feature.available ? 'text.primary' : 'text.secondary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* CTA Button */}
                <Box>
                  {plan.stripeKey === 'event' ? (
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => window.open('mailto:contact@safee.fr?subject=Demande Enterprise', '_blank')}
                      sx={{
                        py: 1.75,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                        color: 'white',
                        border: 'none',
                        boxShadow: `0 4px 12px ${alpha('#FFA500', 0.3)}`,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #FFC700 0%, #FF9500 100%)',
                          boxShadow: `0 6px 16px ${alpha('#FFA500', 0.4)}`,
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  ) : (
                    <CheckoutButton
                      selectedType={plan.stripeKey}
                      duration={isAnnual ? 'annual' : 'monthly'}
                      isAnnual={isAnnual}
                      people={numberOfUsers}
                      planName={plan.name}
                      popular={plan.popular}
                    />
                  )}
                </Box>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Trust badges */}
      <Stack
        direction="row"
        spacing={3}
        justifyContent="center"
        sx={{ mt: 'auto', pb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Security sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Paiement sécurisé via Stripe
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Timer sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            14 jours d'essai gratuit
          </Typography>
        </Stack>
      </Stack>

      {/* Skip button */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="text"
          onClick={onComplete}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.875rem',
            '&:hover': {
              bgcolor: 'transparent',
              textDecoration: 'underline'
            }
          }}
        >
          Continuer sans abonnement (démo)
        </Button>
      </Box>
    </Box>
  );
};